/**
 * SpotCommercialMargin component for managing and displaying commercial margin spot points and amounts.
 * Handles user interactions for editing, resetting, and toggling margin values and currencies.
 * Only visible for Internal Sales user types.
 */

import { isNotDefined } from '@sgme/fp';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import deepEqual from 'fast-deep-equal';
import { useCallback } from 'react';

import { useCurrentUserQuery } from '@/api/user/index.ts';
import { CommercialMarginPanel } from '@/App/tiles/shared/CommercialMargin/CommercialMarginPanel.tsx';
import { createSpotMarginConverter } from '@/App/tiles/shared/CommercialMargin/converters.ts';
import type { MarginSectionProps } from '@/App/tiles/shared/CommercialMargin/MarginSection.tsx';
import { selectCashTileMarginAmountCurrency } from '@/store/cash/selectors/common.ts';
import { selectCashPriceStatus } from '@/store/cash/selectors/prices.ts';
import { cashCommercialMarginCurrencyToggled, cashCommercialSpotMarginUpdated } from '@/store/cash/slice.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';

export type SpotCommercialMarginProps = {
  tileId: string;
  isCommercialMarginVisible: boolean;
  onCommercialMarginVisibilityToggle: () => void;
};

/**
 * SpotCommercialMargin displays and allows editing of commercial margin spot points and amounts for a given tile.
 * Only available for Internal Sales users and when prices are streamed.
 *
 * @example
 * <SpotCommercialMargin tileId="tile-1" isCommercialMarginVisible={true} onCommercialMarginVisibilityToggle={toggleFn} />
 */
export const SpotCommercialMargin = (props: SpotCommercialMarginProps) => {
  const { tileId, isCommercialMarginVisible, onCommercialMarginVisibilityToggle } = props;

  const { sgwtWebAnalytics } = useSgwtWidgets();
  const dispatch = useAppDispatch();
  const { data: user } = useCurrentUserQuery();

  const {
    commercialMarginSpotPoints = { bid: 0, ask: 0 },
    commercialMarginDefaultSpotPoints = { bid: 0, ask: 0 },
    commercialMarginAmount = { bid: 0, ask: 0 },
    commercialMarginAmountCurrency,
    isEditable,
  } = useAppSelector((state) => {
    const priceStatus = selectCashPriceStatus(state, tileId);

    const isEditable = priceStatus.status === 'esp-price' || priceStatus.status === 'rfs-price';

    if (!isEditable) {
      // the currency is still displayed (read-only) before any price is streamed
      return { isEditable: false, commercialMarginAmountCurrency: selectCashTileMarginAmountCurrency(state, tileId) };
    }

    return {
      commercialMarginSpotPoints: priceStatus.commercialMarginSpotPoints,
      commercialMarginDefaultSpotPoints: priceStatus.commercialMarginDefaultSpotPoints,
      commercialMarginAmount: priceStatus.commercialMarginAmount,
      commercialMarginAmountCurrency: priceStatus.commercialMarginAmountCurrency,
      isEditable,
    };
  }, deepEqual);

  const handleUpdateSpotMargin = useCallback(
    (value: { bid: number; ask: number }) => {
      sgwtWebAnalytics?.trackEvent('Cash', 'Commercial margin spot points changed', `${value.bid}/${value.ask}`);

      dispatch(
        cashCommercialSpotMarginUpdated({
          tileId,
          bid: value.bid,
          ask: value.ask,
        }),
      );
    },
    [tileId, sgwtWebAnalytics],
  );

  const handleResetSpotMargin = useCallback(
    (defaultSpotPoints: { bid: number; ask: number }) => {
      sgwtWebAnalytics?.trackEvent(
        'Cash',
        'Commercial margin spot points reseted',
        `${defaultSpotPoints.bid}/${defaultSpotPoints.ask}`,
      );

      dispatch(
        cashCommercialSpotMarginUpdated({
          tileId,
          bid: defaultSpotPoints.bid,
          ask: defaultSpotPoints.ask,
        }),
      );
    },
    [tileId, sgwtWebAnalytics],
  );

  const handleToggleCurrency = useCallback(() => {
    sgwtWebAnalytics?.trackEvent('Cash', 'Commercial margin currency toggled');
    dispatch(cashCommercialMarginCurrencyToggled({ tileId }));
  }, [tileId, sgwtWebAnalytics]);

  // Early return: Only render for Internal Sales users and when prices are streamed
  if (isNotDefined(user?.userType) || user.userType !== 'Internal Sales') {
    return null;
  }

  const sections: MarginSectionProps[] = [
    {
      type: 'spot',
      labelKey: 'CashTile.SpotCommercialMargin.spotPps',
      value: commercialMarginSpotPoints,
      defaultValue: commercialMarginDefaultSpotPoints,
      isEditable,
      increment: 0.1,
      converter: createSpotMarginConverter(),
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
      dataE2ePrefix: 'commercial-margin-spot-pps',
      onUpdate: handleUpdateSpotMargin,
      onReset: commercialMarginDefaultSpotPoints
        ? () => handleResetSpotMargin(commercialMarginDefaultSpotPoints)
        : undefined,
    },
  ];

  return (
    <CommercialMarginPanel
      titleKey="CashTile.SpotCommercialMargin.title"
      isOpened={isCommercialMarginVisible}
      isEditable={isEditable}
      onToggle={onCommercialMarginVisibilityToggle}
      sections={sections}
      marginAmount={commercialMarginAmount}
      currency={commercialMarginAmountCurrency}
      onCurrencyToggle={handleToggleCurrency}
      data-e2e="commercial-margin-toggle-button"
    />
  );
};
